import type { ChangeEvent } from "react";

import iso3166 from "iso-3166-2";
import moment from "moment-timezone";
import type { ControllerRenderProps, FieldValues, Path } from "react-hook-form";

import type { Timeframe } from "../pages/dashboard/home/sections/chart";

const FRACTION_DIGITS_CONFIG = [
  { threshold: 1, digits: 2 },
  { threshold: 0.01, digits: 4 },
  { threshold: 0.0001, digits: 6 },
];
const DEFAULT_DIGITS = 9;

const timeframeFormats: Record<Timeframe, string> = {
  "1D": "hh:mm A z",
  "1W": "MMM DD, yyyy hh:mm A z",
  "1M": "MMM DD, yyyy",
  "1A": "MMM DD, yyyy",
  all: "MMM DD, yyyy",
};

export const getPrettyDate = (
  value?: string | Date | number | null,
  format = "MMM DD, YYYY, hh:mm:ss A z",
) => {
  if (value == null) {
    return "-";
  }
  const date = moment(value);
  if (date.isValid()) {
    return date.format(format);
  } else {
    return "-";
  }
};

export const getPrettyDateFromTimeframe = (
  timeframe: Timeframe,
  epoch?: number,
) => {
  const format = timeframeFormats[timeframe] || timeframeFormats.all;
  return `${moment(epoch).tz(moment.tz.guess()).format(format)}`;
};

export const getPrettyDateFormatted = (
  value = 0,
  format = "MMM DD, yyyy, hh:mm A",
) => `${moment(value).format(format)}`;

export const getPrettyTime = (value: number = 0) =>
  `${moment(value).format("hh:mm A")}`;

const getMaxFractionDigits = (value: number) => {
  const num = Math.abs(value);
  for (const config of FRACTION_DIGITS_CONFIG) {
    if (num === 0 || num > config.threshold) {
      return config.digits;
    }
  }
  return DEFAULT_DIGITS;
};

export const getQuoteCurrency = (symbol: string, assetClass: string) => {
  if (assetClass !== "crypto") {
    return "USD";
  }

  const [base, quote] = symbol.split("/");

  if (!base || !quote) {
    return "";
  }

  return quote;
};

export const getPrettyPrice = (
  value: number | string | undefined,
  symbol: string,
  assetClass: string,
  minimumFractionDigits = 2,
  maximumFractionDigits = 6,
) => {
  const parsed = parseFloat(value?.toString() ?? "");

  if (parsed === 0) {
    return "0.00";
  }

  if (Number.isNaN(parsed) || !Number.isFinite(parsed)) {
    return "-";
  }

  const prefix = parsed < 0 ? "-" : "";

  maximumFractionDigits = Math.max(
    minimumFractionDigits,
    maximumFractionDigits,
  );

  const amount = Math.abs(parsed).toLocaleString("en-US", {
    minimumFractionDigits,
    maximumFractionDigits,
  });

  if (assetClass !== "crypto") {
    return `${prefix}$${amount}`;
  }

  const currency = getQuoteCurrency(symbol, assetClass);

  if (!currency) {
    return `${prefix}${amount}`;
  }

  return `${currency} ${prefix}${amount}`;
};

export const getPrettyCash = (
  value: number | string | null | undefined,
  options?: {
    withPrefix?: boolean;
    forcePrecision?: number;
    minimumFractionDigits?: number;
    acceptZero?: boolean;
  },
): string => {
  const parsed =
    typeof value === "string" ? parseFloat(value.replace(/,/g, "")) : value;

  if (
    parsed === undefined ||
    parsed === null ||
    Number.isNaN(parsed) ||
    !Number.isFinite(parsed) ||
    (parsed === 0 && !options?.acceptZero)
  ) {
    return "–";
  }

  const sign = options?.withPrefix ? (parsed > 0 ? "+" : "-") : "";
  const prefix = parsed < 0 ? "-" : sign;
  const absValue = Math.abs(parsed).toLocaleString("en-US", {
    minimumFractionDigits: options?.minimumFractionDigits || 0,
    maximumFractionDigits:
      options?.forcePrecision || getMaxFractionDigits(parsed),
  });

  return `${prefix}$${absValue}`;
};

export const getPrettyQty = (value: number | string | undefined): string => {
  const parsed = Number(value);
  return Number.isFinite(parsed)
    ? parsed.toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 9,
      })
    : "–";
};

export const getPrettyAmount = (
  value: number | string | null = 0,
  minimumFractionDigits = 2,
  maximumFractionDigits = 2,
  positivePrefix = false,
): string => {
  const parsed = parseFloat(value?.toString() ?? "");

  if (parsed === 0) {
    return "0.00";
  }

  if (Number.isNaN(parsed) || !Number.isFinite(parsed)) {
    return "-";
  }

  maximumFractionDigits = Math.max(
    minimumFractionDigits,
    maximumFractionDigits,
  );

  const prefix = positivePrefix && parsed > 0 ? "+" : "";

  return `${(parsed > 0 ? prefix : "-").concat(
    Math.abs(parsed).toLocaleString("en-US", {
      minimumFractionDigits,
      maximumFractionDigits,
    }),
  )}`;
};

export const getPrettyPercent = (
  value: number | string | null | undefined = 0,
  noPrefix: boolean = false,
  multiply: boolean = false,
): string => {
  const parsed = Number(value);

  if (Number.isNaN(parsed) || !Number.isFinite(parsed) || parsed === 0) {
    return "-";
  }

  return `${parsed > 0 && !noPrefix ? "+" : ""}${(
    multiply ? parsed * 100 : parsed
  ).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`;
};

export const getProfitLossColor = (
  value: string | number | null | undefined,
) => {
  const parsed = Number(value);

  if (Number.isNaN(parsed) || !Number.isFinite(parsed) || parsed === 0) {
    return "text-gray-500";
  }

  // if the value is positive, return green
  if (parsed >= 0) {
    return "text-emerald-500";
  }

  // if the value is negative, return red
  return "text-rose-500";
};

/**
 * @name getNumberOnly
 * @description removes all non-numeric characters from a string
 *
 * @param {string} value
 * @return {number}
 */

export const getNumberOnly = (value = "") =>
  Number(value.replace(/[^0-9.]/g, ""));

/**
 * @name getCashTransformer
 * @description a function that modifies the input of a number value to a currency format (e.g. $1,000.00)
 *
 * @param {object} field - the field object from react-hook-form
 * @param {string} field.value - the value of the field
 * @param {function} field.onChange - the onChange function for the field
 */

export const getCashTransformer = <
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
>({
  field,
}: {
  field: ControllerRenderProps<TFieldValues, TName>;
}) => {
  const transform = {
    // this will probably always be a pass-through
    input: (value: string) => value,
    // this will get called every time the input changes
    output: (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target?.value?.replace(/[^0-9.]/g, "") || "";
      const separator = value.includes(".") ? "." : "";
      const split = value.split(".");
      const base = split[0] !== "" ? parseInt(split[0], 10) : 0;
      const cents = split[1] || "";

      const formattedBase = base.toLocaleString("en-US");

      return `${
        base === 0 && value.startsWith("0") ? "0" : `$${formattedBase}`
      }${separator}${cents.slice(0, 2)}`;
    },
  };

  return {
    ...field,
    placeholder: "$0.00",
    value: transform.input(field.value),
    onChange: (event: ChangeEvent<HTMLInputElement>) =>
      field.onChange(transform.output(event)),
  };
};

/**
 * @name getISO3166Alpha3FormatMapping
 * @description a function that returns ISO3166-ALPHA-2 to ISO3166-ALPHA-3 format
 *
 */

export const getISO3166Alpha3FormatMapping = () => {
  const iso3166Alpha3FormatMapping: Record<string, string> = {};

  Object.keys(iso3166.codes).forEach((key) => {
    const value = iso3166.codes[key];
    iso3166Alpha3FormatMapping[value] = key;
  });

  return iso3166Alpha3FormatMapping;
};

export const getNumericSortValue = (value: string | null | undefined) => {
  if (value == null) {
    return -Infinity;
  }
  const v = parseFloat(value);
  if (Number.isNaN(v)) {
    return -Infinity;
  }
  return v;
};
